import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import gql from 'graphql-tag'
import { useMutation, useQuery } from 'react-apollo'

import UnitDetail from 'src/pages/UnitDetail/UnitDetail'
import UnitDetailPlaceholder from 'src/pages/UnitDetail/UnitDetailPlaceholder'
import DataFetchError from 'src/ui/DataFetchError/DataFetchError'
import { t } from 'src/lib/i18n-react'

const FETCH_UNIT_DETAIL = gql`
  query FETCH_UNIT_DETAIL(
    $unitId: ID!
    $l1Code: LanguageEnum!
    $l2Code: LanguageEnum!
  ) {
    curriculum(native: $l1Code, learning: $l2Code) {
      unit(id: $unitId) {
        id
        name
        iconUrl
        countOfWritingPrompts
        countOfVocabulary
        countOfMedia
        countOfConcepts
        isElective
        exercises {
          id
          name
          imageUrl
          rating
          timesSeen
          lastSeen
        }
        progress {
          isQueued
          isCurrent
          countOfWritingsCompleted
          countOfVocabularySeen
          countOfMediaSeen
          countOfConceptsSeen
          isCompleted
          percentOfExercisesSeen
          countOfExercisesSeen
        }
      }
    }
  }
`

const APPEND_UNIT_TO_QUEUE = gql`
  mutation APPEND_UNIT_TO_QUEUE($unitInput: UnitInputType!) {
    response: appendUnitToQueue(unit: $unitInput) {
      errors {
        code
        message
      }
    }
  }
`

const REMOVE_UNIT_FROM_QUEUE = gql`
  mutation REMOVE_UNIT_FROM_QUEUE($unitInput: UnitInputType!) {
    response: removeUnitFromQueue(unit: $unitInput) {
      status
      errors {
        code
        message
      }
    }
  }
`

const UnitDetailLazy = (props) => {
  const { l1Code, l2Code, unitId, onShowSnackbar } = props
  const [inCourseValue, setInCourseValue] = useState(false)
  const [appendUnitToQueue, { loading: appendMutationLoading }] = useMutation(
    APPEND_UNIT_TO_QUEUE
  )
  const [removeUnitFromQueue, { loading: removeMutationLoading }] = useMutation(
    REMOVE_UNIT_FROM_QUEUE
  )
  const response = useQuery(FETCH_UNIT_DETAIL, {
    variables: {
      unitId,
      l1Code,
      l2Code,
    },
    fetchPolicy: 'network-only',
  })
  const { data, loading } = response
  const unit = data?.curriculum?.unit
  const showExerciseRating =
    data?.curriculum?.unit?.progress?.countOfExercisesSeen > 0
  const isMutationLoading = appendMutationLoading || removeMutationLoading

  useEffect(() => {
    if (unit?.progress) {
      setInCourseValue(unit.progress.isQueued)
    }
  }, [unit, setInCourseValue])

  const handleMutationError = (newValue) => {
    onShowSnackbar({
      message: newValue
        ? t('Error while adding unit to course. Please try again later.')
        : t('Error while removing unit from course. Please try again later.'),
      type: 'error',
    })
  }

  const handleToggleInCourse = async (newValue) => {
    if (isMutationLoading) {
      return
    }

    try {
      const unitInput = { id: unit.id }
      const options = {
        variables: { unitInput },
      }
      let mutationResponse
      if (newValue) {
        mutationResponse = await appendUnitToQueue(options)
      } else {
        mutationResponse = await removeUnitFromQueue(options)
      }

      if (
        !mutationResponse.data?.response ||
        mutationResponse.data?.response?.errors?.length
      ) {
        handleMutationError(newValue)
      } else {
        onShowSnackbar({
          message: newValue
            ? t('Unit successfully added to your course!')
            : t('Unit removed from your course.'),
          type: 'info',
        })
        setInCourseValue(newValue)
      }
    } catch (error) {
      console.log(error)
      handleMutationError(newValue)
    }
  }

  if (loading) {
    return <UnitDetailPlaceholder />
  }

  if (unit) {
    return (
      <UnitDetail
        unit={unit}
        l2Code={l2Code}
        inCourse={inCourseValue}
        showExerciseRating={showExerciseRating}
        onToggleInCourse={handleToggleInCourse}
      />
    )
  }

  return <DataFetchError graphQLResponse={response} />
}

UnitDetailLazy.propTypes = {
  l1Code: PropTypes.string.isRequired,
  l2Code: PropTypes.string.isRequired,
  unitId: PropTypes.string.isRequired,
  onShowSnackbar: PropTypes.func.isRequired,
}

export default UnitDetailLazy
